import { Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { iconDefinitions } from '@ui-lib';

type ColorFilter = 'all' | 'current' | 'fixed';

@Component({
  selector: 'icaredx-svg-icons-page',
  imports: [FormsModule, MatIconModule],
  template: `
    <div class="p-4">
      <h1 class="text-2xl font-bold mb-4">SVG Icons Preview</h1>

      <div class="flex gap-3 mb-4">
        <!-- Search input -->
        <input
          type="text"
          placeholder="Search icons..."
          [(ngModel)]="searchText"
          class="flex-1 p-2 border rounded focus:outline-none focus:ring focus:border-primary" />

        <!-- Color filter tabs -->
        <div class="flex rounded border overflow-hidden text-sm">
          @for (tab of colorFilterTabs; track tab.value) {
            <button
              type="button"
              (click)="colorFilter.set(tab.value)"
              class="px-3 py-2 transition-colors"
              [class]="
                colorFilter() === tab.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              ">
              {{ tab.label }}
            </button>
          }
        </div>
      </div>

      <p class="text-xs text-gray-400 mb-3">
        {{ filteredIcons().length }} icons
      </p>

      <!-- Icons grid -->
      <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
        @for (icon of filteredIcons(); track icon.name) {
          <button
            class="flex flex-col items-center gap-1 p-2 border rounded cursor-pointer hover:bg-gray-50 transition"
            [class]="
              icon.colorMode === 'fixed'
                ? 'border-amber-200 bg-amber-50'
                : 'border-gray-200'
            "
            (click)="copyIconName(icon.name)"
            title="Click to copy name">
            <mat-icon
              [svgIcon]="icon.name"
              class="w-8 h-8 text-primary"></mat-icon>
            <span class="text-xs text-gray-600 text-center wrap-break-words">{{
              icon.name
            }}</span>
          </button>
        }
      </div>
    </div>
  `,
  styles: [
    `
      mat-icon {
        width: 2rem;
        height: 2rem;
      }
    `,
  ],
})
export class SvgIconsPageComponent {
  searchText = '';
  colorFilter = signal<ColorFilter>('all');

  readonly colorFilterTabs: { label: string; value: ColorFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'currentColor', value: 'current' },
    { label: 'Fixed', value: 'fixed' },
  ];

  icons = iconDefinitions;

  filteredIcons() {
    const text = this.searchText.toLowerCase().trim();
    const filter = this.colorFilter();

    return this.icons.filter(icon => {
      if (text && !icon.name.toLowerCase().includes(text)) return false;
      if (filter === 'current') return icon.colorMode !== 'fixed';
      if (filter === 'fixed') return icon.colorMode === 'fixed';
      return true;
    });
  }

  copyIconName(name: string) {
    navigator.clipboard.writeText(name);
    alert(`Copied: ${name}`);
  }
}
